import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography, 
  Link as MuiLink, 
  Paper, 
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fade,
  Container,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  Search,
  Compare,
  Analytics,
  Speed,
  Security,
  TrendingUp,
  CheckCircle
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import logoImage from '../../assets/carconsul-high-resolution-logo-grayscale-transparent.png';

const Login: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const { login, loginWithGoogle, resetPassword } = useAuth();
  const navigate = useNavigate();

  // SEO Meta Tags Setup
  useEffect(() => {
    // Update meta tags for SEO
    document.title = "CarConsul - AI Fleet Management & Car Comparison Platform 2025";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 
        'CarConsul is the best online platform for car price and feature comparison 2025. AI-powered fleet management, intelligent car research, and comprehensive vehicle analysis for smart car buying decisions.'
      );
    }

    const metaKeywords = document.querySelector('meta[name="keywords"]') || document.createElement('meta');
    metaKeywords.setAttribute('name', 'keywords');
    metaKeywords.setAttribute('content', 
      'fleet management ai, ai fleet management, best online platforms for car price and feature comparison 2025, car comparison tool, vehicle analysis, ai car buying assistant, automotive intelligence, car research platform, vehicle pricing comparison, fleet optimization, car features comparison, automotive marketplace, intelligent car search, vehicle evaluation platform'
    );
    if (!document.querySelector('meta[name="keywords"]')) {
      document.head.appendChild(metaKeywords);
    }

    // Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]') || document.createElement('meta');
    ogTitle.setAttribute('property', 'og:title');
    ogTitle.setAttribute('content', 'CarConsul - AI Fleet Management & Car Comparison Platform 2025');
    if (!document.querySelector('meta[property="og:title"]')) {
      document.head.appendChild(ogTitle);
    }

    const ogDescription = document.querySelector('meta[property="og:description"]') || document.createElement('meta');
    ogDescription.setAttribute('property', 'og:description');
    ogDescription.setAttribute('content', 
      'Discover the best online platform for car price and feature comparison 2025. AI-powered fleet management and intelligent vehicle analysis.'
    );
    if (!document.querySelector('meta[property="og:description"]')) {
      document.head.appendChild(ogDescription);
    }

    // Twitter Card tags
    const twitterCard = document.querySelector('meta[name="twitter:card"]') || document.createElement('meta');
    twitterCard.setAttribute('name', 'twitter:card');
    twitterCard.setAttribute('content', 'summary_large_image');
    if (!document.querySelector('meta[name="twitter:card"]')) {
      document.head.appendChild(twitterCard);
    }

    const twitterTitle = document.querySelector('meta[name="twitter:title"]') || document.createElement('meta');
    twitterTitle.setAttribute('name', 'twitter:title');
    twitterTitle.setAttribute('content', 'CarConsul - AI Fleet Management & Car Comparison Platform 2025');
    if (!document.querySelector('meta[name="twitter:title"]')) {
      document.head.appendChild(twitterTitle);
    }

    const twitterDescription = document.querySelector('meta[name="twitter:description"]') || document.createElement('meta');
    twitterDescription.setAttribute('name', 'twitter:description');
    twitterDescription.setAttribute('content', 
      'Best online platform for car price and feature comparison 2025. AI-powered fleet management and vehicle analysis.'
    );
    if (!document.querySelector('meta[name="twitter:description"]')) {
      document.head.appendChild(twitterDescription);
    }

    return () => {
      // Cleanup on unmount
      document.title = "CarConsul - AI Car Buying Assistant";
    };
  }, []);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setError('');
      setLoading(true);
      await login(email, password);
      navigate('/');
    } catch (err: any) {
      setError('Failed to sign in: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setError('');
      setLoading(true);
      await loginWithGoogle();
      navigate('/');
    } catch (err: any) {
      setError('Failed to sign in with Google: ' + (err.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetEmail) {
      setResetMessage('Please enter your email address');
      return;
    }

    try {
      setResetLoading(true);
      setResetMessage('');
      await resetPassword(resetEmail);
      setResetMessage('Password reset email sent! Check your inbox.');
    } catch (err: any) {
      setResetMessage('Error: ' + (err.message || 'Unknown error'));
    } finally {
      setResetLoading(false);
    }
  };

  const openResetDialog = () => {
    setResetDialogOpen(true);
    setResetEmail(email);
    setResetMessage('');
  };

  const features = [
    {
      icon: <Search sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: "AI-Powered Vehicle Search",
      description: "Advanced search algorithms help you find the perfect vehicle based on your specific requirements and budget."
    },
    {
      icon: <Compare sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: "Best Car Price & Feature Comparison 2025",
      description: "Compare prices, features, and specifications across multiple vehicles and dealerships to make informed decisions."
    },
    {
      icon: <Analytics sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: "Fleet Management AI",
      description: "Intelligent fleet optimization with AI-driven insights for cost reduction and efficiency improvement."
    },
    {
      icon: <TrendingUp sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: "Market Intelligence",
      description: "Real-time market trends, pricing analysis, and vehicle depreciation forecasts powered by AI."
    },
    {
      icon: <Security sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: "Comprehensive Vehicle Reports",
      description: "Detailed vehicle history, condition assessments, and dealership analysis for confident purchasing."
    },
    {
      icon: <Speed sx={{ fontSize: 40, color: 'primary.main' }} />,
      title: "Instant Analysis",
      description: "Get instant vehicle evaluations, financing options, and negotiation strategies in real-time."
    }
  ];

  const benefits = [
    "Save up to 30% on vehicle purchases with AI-powered price optimization",
    "Access to the largest database of vehicle listings and pricing data",
    "Professional-grade fleet management tools for businesses",
    "24/7 AI assistant for instant car buying guidance",
    "Comprehensive dealer network analysis and reputation scoring",
    "Advanced filtering and comparison tools for efficient decision making"
  ];

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: 'linear-gradient(to bottom right, #121212, #1E1E1E)',
      }}
    >
      <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          {/* Login form centered at top */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
            <Fade in={true} timeout={800}>
              <Paper
                elevation={6}
                sx={{
                  p: 4,
                  width: '100%',
                  maxWidth: 400,
                  borderRadius: 2,
                  backgroundColor: 'background.paper',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <Box 
                    component="img"
                    src={logoImage}
                    alt="CarConsul Logo"
                    sx={{ 
                      height: 80,
                      maxWidth: '400px',
                      width: 'auto',
                      mb: 2,
                      filter: 'brightness(1.2)',
                      objectFit: 'contain'
                    }}
                  />
                  <Typography component="h1" variant="h5" gutterBottom>
                    Sign in to CarConsul
                  </Typography>
                  {error && (
                    <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
                      {error}
                    </Alert>
                  )}
                  <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1, width: '100%' }}>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="login-email"
                      label="Email Address"
                      name="email"
                      autoComplete="email"
                      autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      name="password"
                      label="Password"
                      type="password"
                      id="login-password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={loading}
                      sx={{ mt: 3, mb: 2 }}
                    >
                      {loading ? 'Signing In...' : 'Sign In'}
                    </Button>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                      <MuiLink 
                        component="button"
                        variant="body2"
                        onClick={openResetDialog}
                        sx={{ textDecoration: 'none', cursor: 'pointer' }}
                      >
                        Forgot password?
                      </MuiLink>
                    </Box>

                    <Divider sx={{ my: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        OR
                      </Typography>
                    </Divider>

                    <Button
                      fullWidth
                      variant="outlined"
                      disabled={loading}
                      onClick={handleGoogleLogin}
                      sx={{ 
                        mb: 2,
                        borderColor: 'divider',
                        color: 'text.primary',
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <Box
                        component="img"
                        src="https://developers.google.com/identity/images/g-logo.png"
                        alt="Google"
                        sx={{ width: 20, height: 20, mr: 2 }}
                      />
                      Continue with Google
                    </Button>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <MuiLink component={Link} to="/signup" variant="body2">
                        {"Don't have an account? Sign Up"}
                      </MuiLink>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Fade>
          </Box>

          {/* CarConsul logo and features below */}
          <Box sx={{ width: '100%', textAlign: 'center' }}>
            {/* CarConsul Logo */}
            <Box sx={{ mb: 4 }}>
              <Box 
                component="img"
                src={logoImage}
                alt="CarConsul Logo"
                sx={{ 
                  height: { xs: 120, md: 160 },
                  maxWidth: '800px',
                  width: 'auto',
                  filter: 'brightness(1.2)',
                  objectFit: 'contain'
                }}
              />
            </Box>

            {/* Features Section */}
            <Box sx={{ width: '100%' }}>
              <Box sx={{ mb: 4 }}>
                <Typography variant={ isMobile ? "h4" : "h3" } component="h1" gutterBottom sx={{ 
                  fontWeight: 700,
                  background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  mb: 2
                }}>
                  AI Fleet Management & Car Comparison Platform 2025
                </Typography>
                <Typography variant={ isMobile ? "body1" : "h6" } color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
                  The best online platform for car price and feature comparison 2025. 
                  Revolutionize your car buying experience with AI-powered insights and comprehensive vehicle analysis.
                </Typography>
              </Box>

              {/* Features Grid */}
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
                gap: 3,
                mb: 4,
                maxWidth: '1200px',
                mx: 'auto'
              }}>
                {features.map((feature, index) => (
                  <Box key={index}>
                    <Fade in={true} timeout={800 + index * 100}>
                      <Card sx={{ 
                        height: '100%', 
                        background: 'rgba(255, 255, 255, 0.05)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid',
                        borderColor: 'divider',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: (theme) => theme.shadows[8],
                          borderColor: 'primary.main'
                        }
                      }}>
                        <CardContent sx={{ p: 3, textAlign: 'center' }}>
                          <Box sx={{ mb: 2 }}>
                            {feature.icon}
                          </Box>
                          <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 600 }}>
                            {feature.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                            {feature.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Fade>
                  </Box>
                ))}
              </Box>

              {/* Benefits */}
              <Card sx={{ 
                background: 'rgba(79, 70, 229, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid',
                borderColor: 'primary.main',
                maxWidth: '800px',
                mx: 'auto'
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h5" component="h2" gutterBottom sx={{ 
                    fontWeight: 600,
                    mb: 3
                  }}>
                    Why Choose CarConsul?
                  </Typography>
                  <List>
                    {benefits.map((benefit, index) => (
                      <ListItem key={index} sx={{ py: 0.5 }}>
                        <ListItemIcon>
                          <CheckCircle sx={{ color: 'primary.main' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={benefit}
                          primaryTypographyProps={{ variant: 'body2', color: 'text.primary' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Forgot Password Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter your email address and we'll send you a link to reset your password.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            variant="outlined"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          {resetMessage && (
            <Alert 
              severity={resetMessage.includes('sent') ? 'success' : 'error'} 
              sx={{ mb: 2 }}
            >
              {resetMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleResetPassword} 
            variant="contained"
            disabled={resetLoading}
          >
            {resetLoading ? 'Sending...' : 'Send Reset Link'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Login;